import { useState, useEffect, useCallback, useRef } from 'react';

const LAST_ACTIVITY_KEY = 'valutten-last-activity';
const CHANNEL_NAME = 'valutten-session';
const ACTIVITY_THROTTLE_MS = 30_000; // Only record activity every 30s
const CHECK_INTERVAL_MS = 15_000; // Check every 15s for responsive countdown

interface UseSessionTimeoutOptions {
  timeoutMs: number;
  warningMs: number;
  onTimeout: () => void;
  enabled: boolean;
}

interface UseSessionTimeoutReturn {
  showWarning: boolean;
  remainingSeconds: number;
  extendSession: () => void;
}

export function useSessionTimeout({
  timeoutMs,
  warningMs,
  onTimeout,
  enabled,
}: UseSessionTimeoutOptions): UseSessionTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const lastThrottleRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);
  const timedOutRef = useRef(false);
  onTimeoutRef.current = onTimeout;

  const recordActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottleRef.current = now;
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    setShowWarning(false);

    // Broadcast to other tabs
    try {
      const bc = new BroadcastChannel(CHANNEL_NAME);
      bc.postMessage({ type: 'activity', timestamp: now });
      bc.close();
    } catch {} // BroadcastChannel not supported — per-tab fallback
  }, []);

  const extendSession = useCallback(() => {
    lastThrottleRef.current = 0; // Force next recordActivity to write
    recordActivity();
  }, [recordActivity]);

  useEffect(() => {
    if (!enabled) {
      setShowWarning(false);
      timedOutRef.current = false;
      return;
    }

    // Initialize last activity
    if (!sessionStorage.getItem(LAST_ACTIVITY_KEY)) {
      sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    }

    // DOM activity listeners
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, recordActivity, { passive: true }));

    // Cross-tab sync
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(CHANNEL_NAME);
      bc.onmessage = (event) => {
        if (event.data?.type === 'activity') {
          sessionStorage.setItem(LAST_ACTIVITY_KEY, String(event.data.timestamp));
          setShowWarning(false);
        }
        if (event.data?.type === 'timeout') {
          timedOutRef.current = true;
          onTimeoutRef.current();
        }
      };
    } catch {} // BroadcastChannel not supported

    // Periodic check
    const intervalId = setInterval(() => {
      if (timedOutRef.current) return;
      const lastActivity = Number(sessionStorage.getItem(LAST_ACTIVITY_KEY) || Date.now());
      const elapsed = Date.now() - lastActivity;
      const remaining = timeoutMs - elapsed;

      if (remaining <= 0) {
        timedOutRef.current = true;
        setShowWarning(false);
        try {
          const notifyBc = new BroadcastChannel(CHANNEL_NAME);
          notifyBc.postMessage({ type: 'timeout' });
          notifyBc.close();
        } catch {}
        onTimeoutRef.current();
      } else if (remaining <= warningMs) {
        setShowWarning(true);
        setRemainingSeconds(Math.ceil(remaining / 1000));
      } else {
        setShowWarning(false);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, recordActivity));
      clearInterval(intervalId);
      bc?.close();
    };
  }, [enabled, timeoutMs, warningMs, recordActivity]);

  return { showWarning, remainingSeconds, extendSession };
}
